import Swal from "sweetalert2";
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  Pagination,
  InputGroup,
  Image,
} from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import {
  PlusCircle,
  PencilSquare,
  Trash,
  People,
  Building,
  Camera,
  FileEarmarkText,
  JournalText,
  Download,
} from "react-bootstrap-icons";
import EmployeeDetails from "../components/EmployeeDetails";
import axios from "axios";
import API_BASE_URL from "../config";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx"; // For CSV export

const ManageEmployees = ({ companyId }) => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const [selectedRole, setSelectedRole] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  // Add state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedLoginBranches, setSelectedLoginBranches] = useState([]); // ← NEW
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    service_ids: [],
    branch_id: null,
    role_id: null,
    department_id: null,
    emergency_contact_name: "",
    emergency_contact_phone: "",
    notes: "",
    date_of_joining: "",
    commission_rate: "", // ← ADD
    login_enabled: false, // ← ADD
    password: "", // ← ADD
  });

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/branches`
      );
      setBranches(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load branches", err);
      setBranches([]);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/roles`
      );
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load roles", err);
      setRoles([]);
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/departments`
      );
      setDepartments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load departments", err);
      setDepartments([]);
    }
  };

  // Fetch employees
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/employees`
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load employees");
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load employees",
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchEmployee = async (employeeId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/employees/${employeeId}`);
      return res.data;
    } catch (err) {
      console.error("Failed to fetch employee", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load employee details",
      });
      return null;
    }
  };
  // Fetch services (with branch filter)
  const fetchServices = async (branchId = null) => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/services`,
        { params }
      );
      setServices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load services", err);
      setServices([]);
    }
  };

  // Fetch documents for an employee
  const fetchDocuments = async (employeeId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/documents`
      );
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load documents", err);
      setDocuments([]);
    }
  };

  // Fetch logs for an employee
  const fetchLogs = async (employeeId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/logs`
      );
      setLogs(Array.isArray(res.data) ? res.data : []);
      setShowLogsModal(true);
    } catch (err) {
      console.error("Failed to load logs", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load activity logs",
      });
    }
  };

  useEffect(() => {
    if (!companyId) return;
    fetchEmployees();
    fetchBranches();
    fetchRoles();
    fetchDepartments();
    fetchServices();
    fetchCountries();
  }, [companyId]);

  // Handle search filtering
  useEffect(() => {
    const filtered = employees.filter(
      (emp) =>
        `${emp.first_name} ${emp.last_name}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (emp.phone && emp.phone.includes(searchQuery))
    );
    setFilteredEmployees(filtered);
    setCurrentPage(1);
  }, [searchQuery, employees]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEmployees.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const handleBranchSelect = (selected) => {
    setSelectedBranch(selected);
    const branchId = selected[0]?.id || null;
    setForm({ ...form, branch_id: branchId });
    fetchServices(branchId);
  };

  const handleRoleSelect = (selected) => {
    setSelectedRole(selected);
    setForm({ ...form, role_id: selected[0]?.id || null });
  };

  const handleDepartmentSelect = (selected) => {
    setSelectedDepartment(selected);
    setForm({ ...form, department_id: selected[0]?.id || null });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleServiceToggle = (serviceId) => {
    const id = Number(serviceId);
    const updated = form.service_ids.includes(id)
      ? form.service_ids.filter((s) => s !== id)
      : [...form.service_ids, id];
    setForm({ ...form, service_ids: updated });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const uploadPhoto = async (employeeId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("photo", file);
    try {
      await axios.post(
        `${API_BASE_URL}/employees/${employeeId}/photo`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
    } catch (err) {
      console.error("Failed to upload photo", err);
      setError("Failed to upload photo");
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingEmployee) return;
    const formData = new FormData();
    formData.append("document", file);
    try {
      await axios.post(
        `${API_BASE_URL}/employees/${editingEmployee.id}/documents`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      fetchDocuments(editingEmployee.id);
      Swal.fire({
        icon: "success",
        title: "Uploaded!",
        text: "Document uploaded successfully",
        timer: 1500,
      });
    } catch (err) {
      console.error("Failed to upload document", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to upload document",
      });
    }
  };

  const handleDownloadDocument = (docId, fileName) => {
    window.location.href = `${API_BASE_URL}/documents/${docId}`;
  };
  // Handle click
  const handleEmployeeClick = (emp) => {
    setSelectedEmployee(emp);
    setShowDetailsModal(true);
  };
  const validate = () => {
    if (!form.first_name) return "First name is required";
    if (!form.last_name) return "Last name is required";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Valid email is required";
    if (form.phone && !/^\+?[1-9]\d{1,14}$/.test(form.phone))
      return "Invalid phone format";
    if (
      form.emergency_contact_phone &&
      !/^\+?[1-9]\d{1,14}$/.test(form.emergency_contact_phone)
    )
      return "Invalid emergency phone format";
    return null;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setError(null);

  // === VALIDATION ===
  if (!form.first_name) return setError("First name is required");
  if (!form.last_name) return setError("Last name is required");
  if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    return setError("Valid email is required");

  // Only require password if: new manager + login enabled
  if (form.login_enabled && !editingEmployee && !form.password) {
    return setError("Password is required for new manager login");
  }

  // === BUILD PAYLOAD ===
  const payload = {
    // Core employee fields
    first_name: form.first_name,
    last_name: form.last_name,
    email: form.email,
    phone: form.phone || null,
    emergency_contact_name: form.emergency_contact_name || null,
    emergency_contact_phone: form.emergency_contact_phone || null,
    notes: form.notes || null,
    date_of_joining: form.date_of_joining || null,

    // Foreign keys
    country_id: selectedCountry[0]?.id || null,
    role_id: selectedRole[0]?.id || null,
    branch_id: selectedBranch[0]?.id || null,           // ← Work location
    department_id: selectedDepartment[0]?.id || null,
    commission_rate: form.commission_rate ? parseFloat(form.commission_rate) : null,
    service_ids: form.service_ids,

    // Manager login
    login_enabled: form.login_enabled,
    login_branch_ids: selectedLoginBranches.map(b => b.id), // ← MULTI-BRANCH ACCESS
  };

  // Only send password if filled (edit or new)
  if (form.password) {
    payload.password = form.password;
  }

  try {
    let employeeId;
    if (editingEmployee) {
      await axios.put(
        `${API_BASE_URL}/employees/${editingEmployee.id}`,
        payload
      );
      employeeId = editingEmployee.id;
    } else {
      const res = await axios.post(
        `${API_BASE_URL}/companies/${companyId}/employees`,
        payload
      );
      employeeId = res.data.id;
    }

    // Upload photo if changed
    const photoFile = e.target.photo?.files[0];
    if (photoFile) {
      await uploadPhoto(employeeId, photoFile);
    }

    Swal.fire({
      icon: "success",
      title: editingEmployee ? "Updated!" : "Added!",
      text: `Employee ${editingEmployee ? "updated" : "added"} successfully`,
      timer: 1500,
    });
    setShowModal(false);
    fetchEmployees();
  } catch (err) {
    console.error(err);
    setError(err.response?.data?.message || "Failed to save employee");
    Swal.fire({
      icon: "error",
      title: "Error!",
      text: err.response?.data?.message || "Failed to save employee",
    });
  }
};
const handleEdit = async (emp) => {
  setLoading(true);
  const fullEmp = await fetchEmployee(emp.id);
  if (!fullEmp) {
    setLoading(false);
    return;
  }

  const serviceIds = Array.isArray(fullEmp.services)
    ? fullEmp.services.map((s) => s.id)
    : [];

  // === LOAD LOGIN BRANCH IDS FROM API ===
  const loginBranchIds = Array.isArray(fullEmp.login_branch_ids)
    ? fullEmp.login_branch_ids
    : [];

  setEditingEmployee(fullEmp);
  setForm({
    first_name: fullEmp.first_name || "",
    last_name: fullEmp.last_name || "",
    phone: fullEmp.phone || "",
    email: fullEmp.email || "",
    service_ids: serviceIds,
    branch_id: fullEmp.branch_id || null,
    role_id: fullEmp.role_id || null,
    department_id: fullEmp.department_id || null,
    emergency_contact_name: fullEmp.emergency_contact_name || "",
    emergency_contact_phone: fullEmp.emergency_contact_phone || "",
    notes: fullEmp.notes || "",
    date_of_joining: fullEmp.date_of_joining || "",
    commission_rate: fullEmp.commission_rate || "",
    login_enabled: !!fullEmp.login_enabled,
    password: "",
  });

  // === TYPEAHEAD: ASSIGNED BRANCH (WORK LOCATION) ===
  const assignedBranch = branches.find((b) => b.id === fullEmp.branch_id);
  setSelectedBranch(assignedBranch ? [assignedBranch] : []);

  // === TYPEAHEAD: LOGIN ACCESS BRANCHES (MULTI) ===
  const loginBranches = branches.filter((b) => loginBranchIds.includes(b.id));
  setSelectedLoginBranches(loginBranches);

  // === OTHER DROPDOWNS ===
  const role = roles.find((r) => r.id === fullEmp.role_id);
  setSelectedRole(role ? [role] : []);
  const dept = departments.find((d) => d.id === fullEmp.department_id);
  setSelectedDepartment(dept ? [dept] : []);
  const country = countries.find((c) => c.id === fullEmp.country_id);
  setSelectedCountry(country ? [country] : []);

  setPhotoPreview(
    fullEmp.profile_photo ? `${API_BASE_URL}${fullEmp.profile_photo}` : null
  );
  await fetchDocuments(fullEmp.id);
  setShowModal(true);
  setLoading(false);
};

  const handleAdd = () => {
    setEditingEmployee(null);
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      service_ids: [],
      branch_id: null,
      role_id: null,
      department_id: null,
      emergency_contact_name: "",
      emergency_contact_phone: "",
      notes: "",
      date_of_joining: "",
    });
    setSelectedBranch([]);
    setSelectedRole([]);
    setSelectedDepartment([]);
    setPhotoPreview(null);
    setDocuments([]);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/employees/${id}`);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Employee has been deleted.",
        timer: 1500,
      });
      fetchEmployees();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete employee",
      });
    }
  };

  const exportToCSV = () => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const ws = XLSX.utils.json_to_sheet(
      employees.map((emp) => ({
        Name: `${emp.first_name} ${emp.last_name}`,
        Email: emp.email,
        Phone: emp.phone,
        Branch: emp.branch_name || "—",
        Role: emp.role_name || "—",
        Department: emp.department_name || "—",
        "Date of Joining": emp.date_of_joining || "—",
        Services: emp.services.map((s) => s.name).join(", ") || "—",
      }))
    );
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, "employees" + fileExtension);
  };
  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/countries`);
      const india = res.data.find((c) => c.code === "IN");
      setCountries(res.data);
      if (india) setSelectedCountry([india]);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="container-fluid mt-3 px-3 px-md-4">
      <Row className="align-items-center mb-3">
        <Col xs={12} md={6}>
          <h2 className="mb-0">
            <People className="me-2" /> Manage Employees
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={exportToCSV}
            className="me-2"
          >
            <Download className="me-1" /> Export CSV
          </Button>
          <Button variant="primary" size="sm" onClick={handleAdd}>
            <PlusCircle className="me-1" /> Add Employee
          </Button>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" /> Loading
          employees...
        </div>
      ) : (
        <Card className="shadow-sm border-0">
          <div className="table-responsive">
            <Table striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th className="col-3 col-md-2">Name</th>
                  <th className="col-3 col-md-2 d-none d-md-table-cell">
                    Email
                  </th>
                  <th className="col-2 col-md-2 d-none d-lg-table-cell">
                    Phone
                  </th>
                  <th className="col-2 col-md-2">Branch</th>
                  <th className="col-2 col-md-2 d-none d-md-table-cell">
                    Role/Dept
                  </th>
                  <th className="col-2 col-md-2 d-none d-md-table-cell">
                    Services
                  </th>
                  <th className="col-2 col-md-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <People className="mb-2" size={48} />
                      <p className="text-muted mb-0">No employees found</p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((emp) => (
                    <tr key={emp.id}>
                      <td
                        onClick={() => handleEmployeeClick(emp)}
                        style={{ cursor: "pointer" }}
                      >
                        {emp.profile_photo && (
                          <Image
                            src={`${API_BASE_URL}${emp.profile_photo}`}
                            roundedCircle
                            width={32}
                            height={32}
                            className="me-2"
                          />
                        )}
                        <strong>
                          {emp.first_name} {emp.last_name}
                        </strong>
                      </td>
                      <td className="d-none d-md-table-cell">{emp.email}</td>
                      <td className="d-none d-lg-table-cell">{emp.phone}</td>
                      <td>{emp.branch_name || "—"}</td>
                      <td className="d-none d-md-table-cell">
                        {emp.role_name || "—"} / {emp.department_name || "—"}
                      </td>
                      <td className="d-none d-md-table-cell">
                        {Array.isArray(emp.services) && emp.services.length > 0
                          ? emp.services.map((s) => s.name).join(", ")
                          : "—"}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => handleEdit(emp)}
                            title="Edit"
                          >
                            <PencilSquare size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(emp.id)}
                            title="Delete"
                          >
                            <Trash size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => fetchLogs(emp.id)}
                            title="View Logs"
                          >
                            <JournalText size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          <Pagination className="justify-content-center mt-3">
            {Array.from({ length: totalPages }, (_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <People className="me-2" />{" "}
              {editingEmployee ? "Edit Employee" : "Add Employee"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            <Row>
              <Col xs={12} md={4} className="mb-3 text-center">
                <Form.Label className="fw-bold">Profile Photo</Form.Label>
                <div className="d-flex flex-column align-items-center">
                  {photoPreview ? (
                    <Image
                      src={photoPreview}
                      roundedCircle
                      width={150}
                      height={150}
                      className="mb-2"
                    />
                  ) : (
                    <Camera size={150} className="text-muted mb-2" />
                  )}
                  <Form.Control
                    type="file"
                    name="photo"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="mt-2"
                  />
                </div>
              </Col>
              <Col xs={12} md={8}>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      First Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      required
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      Last Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">Role</Form.Label>
                    <Typeahead
                      id="role-select"
                      labelKey="name"
                      options={roles}
                      placeholder="Select role..."
                      selected={selectedRole}
                      onChange={handleRoleSelect}
                      clearButton
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">Department</Form.Label>
                    <Typeahead
                      id="department-select"
                      labelKey="name"
                      options={departments}
                      placeholder="Select department..."
                      selected={selectedDepartment}
                      onChange={handleDepartmentSelect}
                      clearButton
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Date of Joining</Form.Label>
                    <Form.Control
                      type="date"
                      name="date_of_joining"
                      value={form.date_of_joining}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      Commission Rate (%)
                    </Form.Label>
                    <Form.Control
                      name="commission"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={form.commission_rate || ""}
                      onChange={(e) =>
                        setForm({ ...form, commission_rate: e.target.value })
                      }
                    />
                  </Col>
                </Row>
                {branches.length > 0 && (
                  <Row>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="fw-bold">Branch</Form.Label>
                      <Typeahead
                        id="branch-select"
                        labelKey="name"
                        options={branches}
                        placeholder="Select branch..."
                        selected={selectedBranch}
                        onChange={handleBranchSelect}
                        clearButton
                      />
                    </Col>
                  </Row>
                )}
                {countries.length > 0 && (
                  <Row>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="fw-bold">
                        Country <span className="text-danger">*</span>
                      </Form.Label>
                      <Typeahead
                        id="country-select"
                        labelKey="name"
                        options={countries}
                        placeholder="Select country..."
                        selected={selectedCountry}
                        onChange={(sel) => {
                          setSelectedCountry(sel);
                          setForm({
                            ...form,
                            country_id: sel[0]?.id || null,
                          });
                        }}
                        clearButton
                      />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col xs={12} className="mb-3">
                    <Form.Label className="fw-bold">Assign Services</Form.Label>
                    <div className="row">
                      {services.map((s) => (
                        <Col xs={12} md={6} lg={4} key={s.id} className="mb-2">
                          <Form.Check
                            type="checkbox"
                            label={s.name}
                            checked={form.service_ids.includes(s.id)}
                            onChange={() => handleServiceToggle(s.id)}
                          />
                        </Col>
                      ))}
                    </div>
                  </Col>
                </Row>
                {selectedRole[0]?.name === "Manager" && (
                  <>
                    {/* === LOGIN ENABLED SWITCH === */}
                    <Row>
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Label>Login Enabled</Form.Label>
                        <Form.Check
                          type="switch"
                          checked={form.login_enabled || false}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              login_enabled: e.target.checked,
                            })
                          }
                        />
                      </Col>
                    </Row>

                    {/* === LOGIN ACCESS BRANCHES (MULTI) === */}
                    {form.login_enabled && (
                      <>
                        <Row>
                          <Col xs={12} className="mb-3">
                            <Form.Label className="fw-bold text-primary">
                              Login Access Branches
                            </Form.Label>
                            <Typeahead
                              multiple
                              id="login-branches"
                              labelKey="name"
                              options={branches}
                              placeholder="Select branches this manager can log into"
                              selected={selectedLoginBranches}
                              onChange={setSelectedLoginBranches}
                              clearButton
                            />
                            <Form.Text className="text-muted">
                              Manager will only see data from selected branches
                              after login
                            </Form.Text>
                          </Col>
                        </Row>

                        <Row>
                          <Col xs={12} md={6} className="mb-3">
                            <Form.Label>
                              Password{" "}
                              {editingEmployee &&
                                "(leave blank to keep current)"}
                            </Form.Label>
                            <Form.Control
                              type="password"
                              value={form.password || ""}
                              onChange={(e) =>
                                setForm({ ...form, password: e.target.value })
                              }
                              placeholder={
                                editingEmployee
                                  ? "Enter new password or leave blank"
                                  : "Required for first-time login"
                              }
                            />
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* === ASSIGNED BRANCH (WORK LOCATION) === */}
                    {/* <Row>
                      <Col xs={12} className="mb-3">
                        <Form.Label>Assigned Branch (Optional)</Form.Label>
                        <Typeahead
                          id="assigned-branch"
                          labelKey="name"
                          options={branches}
                          placeholder="Select where this employee works"
                          selected={selectedBranch}
                          onChange={handleBranchSelect}
                          clearButton
                        />
                        <Form.Text className="text-muted">
                          This is the employee's primary work location (not
                          login scope)
                        </Form.Text>
                      </Col>
                    </Row> */}
                  </>
                )}
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Emergency Contact Name</Form.Label>
                    <Form.Control
                      name="emergency_contact_name"
                      value={form.emergency_contact_name}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Emergency Contact Phone</Form.Label>
                    <Form.Control
                      name="emergency_contact_phone"
                      value={form.emergency_contact_phone}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="mb-3">
                    <Form.Label>Internal Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>
                {editingEmployee && (
                  <Row>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="fw-bold">Documents</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={handleDocumentUpload}
                        className="mb-2"
                      />
                      <ul className="list-group">
                        {documents.map((doc) => (
                          <li
                            key={doc.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <FileEarmarkText className="me-2" /> {doc.file_name}
                            <Button
                              variant="link"
                              onClick={() =>
                                handleDownloadDocument(doc.id, doc.file_name)
                              }
                            >
                              Download
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Saving...
                </>
              ) : editingEmployee ? (
                "Update"
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Logs Modal */}
      <Modal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Employee Activity Logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped hover>
            <thead>
              <tr>
                <th>Action</th>
                <th>Details</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center">
                    No logs found
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => (
                  <tr key={idx}>
                    <td>{log.action}</td>
                    <td>{log.details}</td>
                    <td>{new Date(log.created_at).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      <EmployeeDetails
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        employee={selectedEmployee}
      />
    </div>
  );
};

export default ManageEmployees;
